import { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import './PricingSettings.css';

const DEFAULT_CONFIG = {
  enabled: true,
  showBreakdown: false,
  currency: 'EUR',
  basePrice: 200,
  detailedMissionBonus: 15,
  keywordBonus: 10,           // €10 per keyword
  darkModeBonus: 10,
  fontPricing: {
    sans: 0,
    serif: 0,
    mono: 0,
    display: 0,
    rounded: 0,
    elegant: 10               // Only luxury adds cost
  },
  denseThreshold: 70,
  denseBonus: 25,
  playfulThreshold: 30,
  playfulBonus: 20,
  experimentalThreshold: 70,
  experimentalBonus: 35,
  complexityMultiplier: 1.08,
  creativeLuxuryMultiplier: 1.05
};

export default function PricingSettings() {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch('/api/admin/settings');
      if (res.ok) {
        const data = await res.json();
        setConfig({ ...DEFAULT_CONFIG, ...data.pricing });
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing: config })
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  // Auto-save when enabled state changes
  const handleToggleEnabled = async (enabled) => {
    const newConfig = { ...config, enabled };
    setConfig(newConfig);
    
    // Immediately save to persist the toggle state
    try {
      await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pricing: newConfig })
      });
      setMessage({ type: 'success', text: enabled ? 'Pricing enabled!' : 'Pricing disabled!' });
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save toggle state' });
    }
  };

  const updateFontPricing = (font, value) => {
    setConfig(prev => ({
      ...prev,
      fontPricing: { ...prev.fontPricing, [font]: parseInt(value) || 0 }
    }));
  };

  if (loading) {
    return (
      <div className="pricing-settings pricing-settings--loading">
        <div className="pricing-settings__spinner" />
        Loading settings...
      </div>
    );
  }

  return (
    <div className="pricing-settings">
      <div className="pricing-settings__header">
        <div>
          <h2>Pricing Configuration</h2>
          <p>Configure how estimated prices are calculated for briefs</p>
        </div>
        <div className="pricing-settings__toggle">
          <label className="pricing-settings__switch">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => handleToggleEnabled(e.target.checked)}
            />
            <span className="pricing-settings__slider"></span>
          </label>
          <span className={`pricing-settings__status ${config.enabled ? 'pricing-settings__status--on' : ''}`}>
            {config.enabled ? 'Pricing Enabled' : 'Pricing Disabled'}
          </span>
        </div>
      </div>

      {message && (
        <div className={`pricing-settings__message pricing-settings__message--${message.type}`}>
          {message.text}
        </div>
      )}

      <div className={`pricing-settings__content ${!config.enabled ? 'pricing-settings__content--disabled' : ''}`}>
        {/* General Settings */}
        <Card className="pricing-settings__card">
          <CardHeader>
            <CardTitle>General Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pricing-settings__grid">
              <div className="pricing-settings__field">
                <label>Base Price (€)</label>
                <input
                  type="number"
                  value={config.basePrice}
                  onChange={(e) => updateConfig('basePrice', parseInt(e.target.value) || 0)}
                  min="0"
                  step="50"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Currency</label>
                <select
                  value={config.currency}
                  onChange={(e) => updateConfig('currency', e.target.value)}
                  disabled
                >
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              <div className="pricing-settings__field pricing-settings__field--checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={config.showBreakdown}
                    onChange={(e) => updateConfig('showBreakdown', e.target.checked)}
                  />
                  Show price breakdown to users
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Brand Core Bonuses */}
        <Card className="pricing-settings__card">
          <CardHeader>
            <CardTitle>Brand Core Bonuses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pricing-settings__grid">
              <div className="pricing-settings__field">
                <label>Detailed Mission Bonus (€)</label>
                <span className="pricing-settings__hint">Applied when mission &gt; 200 chars</span>
                <input
                  type="number"
                  value={config.detailedMissionBonus}
                  onChange={(e) => updateConfig('detailedMissionBonus', parseInt(e.target.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Per Keyword Bonus (€)</label>
                <span className="pricing-settings__hint">€10 × number of keywords (max 3 = €30)</span>
                <input
                  type="number"
                  value={config.keywordBonus}
                  onChange={(e) => updateConfig('keywordBonus', parseInt(e.target.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Visual Bonuses */}
        <Card className="pricing-settings__card">
          <CardHeader>
            <CardTitle>Visual Bonuses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pricing-settings__grid">
              <div className="pricing-settings__field">
                <label>Dark Mode Bonus (€)</label>
                <input
                  type="number"
                  value={config.darkModeBonus}
                  onChange={(e) => updateConfig('darkModeBonus', parseInt(e.target.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Luxury Font Bonus (€)</label>
                <span className="pricing-settings__hint">Only "Elegant" font style adds cost</span>
                <input
                  type="number"
                  value={config.fontPricing?.elegant || 0}
                  onChange={(e) => updateFontPricing('elegant', e.target.value)}
                  min="0"
                  step="5"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scale Bonuses */}
        <Card className="pricing-settings__card">
          <CardHeader>
            <CardTitle>Design Scale Bonuses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pricing-settings__grid">
              <div className="pricing-settings__field">
                <label>Dense Threshold (%)</label>
                <input
                  type="number"
                  value={config.denseThreshold}
                  onChange={(e) => updateConfig('denseThreshold', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Dense Bonus (€)</label>
                <input
                  type="number"
                  value={config.denseBonus}
                  onChange={(e) => updateConfig('denseBonus', parseInt(e.target.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Playful Threshold (%)</label>
                <span className="pricing-settings__hint">Below this = playful</span>
                <input
                  type="number"
                  value={config.playfulThreshold}
                  onChange={(e) => updateConfig('playfulThreshold', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Playful Bonus (€)</label>
                <input
                  type="number"
                  value={config.playfulBonus}
                  onChange={(e) => updateConfig('playfulBonus', parseInt(e.target.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Experimental Threshold (%)</label>
                <input
                  type="number"
                  value={config.experimentalThreshold}
                  onChange={(e) => updateConfig('experimentalThreshold', parseInt(e.target.value) || 0)}
                  min="0"
                  max="100"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Experimental Bonus (€)</label>
                <input
                  type="number"
                  value={config.experimentalBonus}
                  onChange={(e) => updateConfig('experimentalBonus', parseInt(e.target.value) || 0)}
                  min="0"
                  step="5"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Multipliers */}
        <Card className="pricing-settings__card">
          <CardHeader>
            <CardTitle>Complexity Multipliers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pricing-settings__grid">
              <div className="pricing-settings__field">
                <label>High Complexity (×)</label>
                <span className="pricing-settings__hint">Dense + Experimental</span>
                <input
                  type="number"
                  value={config.complexityMultiplier}
                  onChange={(e) => updateConfig('complexityMultiplier', parseFloat(e.target.value) || 1)}
                  min="1"
                  max="2"
                  step="0.05"
                />
              </div>
              <div className="pricing-settings__field">
                <label>Creative Luxury (×)</label>
                <span className="pricing-settings__hint">Playful + Elegant font</span>
                <input
                  type="number"
                  value={config.creativeLuxuryMultiplier}
                  onChange={(e) => updateConfig('creativeLuxuryMultiplier', parseFloat(e.target.value) || 1)}
                  min="1"
                  max="2"
                  step="0.05"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="pricing-settings__actions">
        <Button variant="secondary" onClick={handleReset}>
          Reset to Defaults
        </Button>
        <Button variant="primary" onClick={handleSave} loading={saving}>
          Save Settings
        </Button>
      </div>
    </div>
  );
}
